import { SerializedStyles } from "@emotion/react";
import { ReactNode, Ref, memo, useCallback, useMemo } from "react";
import { FormFlowPage, InfoAlert, RadioGroup, ToggleableBigButton } from "src/components";
import { useAnimatedSelect } from "src/hooks";
import { createStyles } from "src/styles";
import { noInput } from "src/utilities/input";

import { colors, enums, formatters } from "@fraction/shared";

export interface SingleSelectQuestionLayoutProps {
  firstName?: string;
  creditBureau?: enums.CreditBureau;
  title: string;
  value?: string;
  options: { value: string; label: string }[];
  focusRef?: Ref<HTMLInputElement>;
  onChange: (value?: string) => void;
  onNext: (val?: string) => void;
  onBack: () => void;
  style?: SerializedStyles;
  children?: ReactNode;
  nextLoading?: boolean;
}

const styles = createStyles({
  alert: {
    marginBottom: 40,
  },
  radio: {
    marginTop: 12,
    marginBottom: 40,
  },
  button: { marginBottom: 6 },
  buttonText: {
    color: colors.palette.GREY_700,
  },
  headerStyle: {
    "@media(max-width: 600px)": {
      fontSize: 28,
    },
  },
});

const SingleSelectQuestionLayout = ({
  onChange,
  value,
  options,
  firstName,
  creditBureau,
  focusRef,
  onNext,
  onBack,
  title,
  style,
  children,
  nextLoading,
  ...props
}: SingleSelectQuestionLayoutProps) => {
  const bureau = creditBureau ? formatters.enums.CREDIT_BUREAU_MAP[creditBureau] : "the credit bureaus";

  const { onChangePageValue } = useAnimatedSelect({ onChange, onNext });

  const handleNext = useCallback(
    (event: React.ChangeEvent<HTMLFormElement>) => {
      event?.preventDefault();
      onNext?.(value);
    },
    [onNext, value]
  );

  const formattedPhoneOptions = useMemo(() => {
    if (!title.includes("phone number")) {
      return options || [];
    }
    // format phone numbers if this includes a phone number
    return (options || []).map((option) => ({
      ...option,
      label: formatters.phone.formatPhoneNumber(option.label),
    }));
  }, [title, options]);

  const formattedOptions = useMemo(() => {
    return formattedPhoneOptions.map((option) => {
      if (option.label === "NONE OF THE ABOVE") {
        return {
          ...option,
          label: "None of the above",
        };
      }
      return option;
    });
  }, [formattedPhoneOptions]);

  return (
    <FormFlowPage
      {...props}
      title={title}
      description="To protect your privacy, and in accordance with credit data regulations, we need to verify your identity before continuing."
      onNext={handleNext}
      onBack={onBack}
      style={style}
      headerStyle={styles.headerStyle}
      nextDisabled={noInput(value)}
      nextLoading={nextLoading}
    >
      <RadioGroup
        focusRef={focusRef}
        style={styles.radio}
        value={value?.toString()}
        onChange={onChangePageValue}
      >
        {formattedOptions.map((option) => (
          <ToggleableBigButton
            animatedSelect
            style={styles.button}
            key={option.value}
            label={option.label}
            value={option.value}
          />
        ))}
      </RadioGroup>
      <InfoAlert style={styles.alert}>
        {`We use this information to validate your identity using information that only you and ${
          bureau || "the credit bureaus"
        } should
        know. This is not a credit check and we do not store any personal information from these questions.`}
      </InfoAlert>
      {children}
    </FormFlowPage>
  );
};

export default memo(SingleSelectQuestionLayout);
